import { useState, useRef, useEffect } from 'react'
import { OperationVariables, QueryResult } from '@apollo/client'

export type RetryState = 'idle' | 'retrying' | 'success'

export interface RetryInfo {
  retryCount: number
  retryState: RetryState
}

type RetryEvent<Data, TReturn = void> = (
  result: Pick<QueryResult<Data>, 'data' | 'error'> & { retryCount: number }
) => TReturn

export interface RetryQueryProps<Data> {
  retryMax?: number
  retryTimeout?: number
  retryIf?: RetryEvent<Data, boolean>
}

/**
 * A hook to add automatic retry logic to an Apollo useQuery call.
 * @param queryResult - The result object returned from useQuery.
 * @param options - Configuration for the retry mechanism.
 * @returns The original queryResult expanded with retry information.
 */
export const useQueryRetry = <
  Data,
  Variables extends OperationVariables = OperationVariables,
>(
  queryResult: QueryResult<Data, Variables>,
  options: RetryQueryProps<Data> = {}
): QueryResult<Data, Variables> & RetryInfo => {
  const {
    retryMax = 5,
    retryTimeout = 3000,
    retryIf = result => !!result.error,
  } = options

  const { error, data, refetch, loading } = queryResult

  const [retryCount, setRetryCount] = useState(0)
  const [retryState, setRetryState] = useState<RetryState>('idle')
  const timeoutRef = useRef<number | null>(null)

  const passDownRefs = useRef({
    refetch,
    maxAttempts: retryMax,
    delay: retryTimeout,
    retryIf,
    retryCount,
    retryState,
  })
  passDownRefs.current = {
    refetch,
    maxAttempts: retryMax,
    delay: retryTimeout,
    retryIf,
    retryCount,
    retryState,
  }

  useEffect(() => {
    const { refetch, maxAttempts, delay, retryIf, retryCount, retryState } =
      passDownRefs.current

    // If the query is loading for the first time, it's idle.
    if (loading && retryCount === 0) {
      setRetryState('idle')
      return
    }

    // Don't process while loading from a refetch
    if (loading) return

    const retryContext = { data, error, retryCount }
    const shouldRetry = retryCount < maxAttempts && retryIf(retryContext)

    // If we should retry and we're not already retrying
    if (shouldRetry && retryState !== 'retrying') {
      setRetryState('retrying')

      // Schedule the next retry
      timeoutRef.current = window.setTimeout(() => {
        setRetryCount(prev => prev + 1)
        refetch()
      }, delay)
    } else if (!shouldRetry) {
      // If we shouldn't retry, the query is considered successful
      setRetryCount(0)
      setRetryState('success')
    }

    // Cleanup function to clear the timeout if the component unmounts
    // or if the retry state changes before the timeout completes.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [error, data, loading])

  return { ...queryResult, retryCount, retryState }
}
